<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useCacheStore } from '@/modules/cache/stores/cache.store'
import { useSettingStore } from '@/modules/setting/stores/setting.store'
import { useClientStore } from '@/modules/client/stores/client.store'
import { useSideMenuStore } from '@/modules/side-menu/stores/side-menu'
import { useSearchStore } from '@/modules/search/stores/search.store'
import { REFRESH_HOME, REFRESH_MOTION } from '@/constants'
import IndeterminateLoader from '@/shared/components/loader/IndeterminateLoader.vue'
import VersionUpdateMenu from '@/modules/version/components/VersionUpdateMenu.vue'
import VersionUpdateDialog from '@/modules/version/components/VersionUpdateDialog.vue'
import ClientSelectInput from '@/modules/client/components/ClientInputSelect.vue'
import UserNotificationMenu from '@/modules/user/components/UserNotificationMenu.vue'
import AlertMenu from '@/modules/setting/components/AlertMenu.vue'
import UserMenu from '@/modules/user/components/UserMenu.vue'
import ViewMenu from '@/modules/view/components/ViewMenu.vue'
import SearchBar from '@/modules/search/components/SearchBar.vue'
import eventBus from '@/event-bus'

const route = useRoute()
const appStore = useAppStore()
const userStore = useUserStore()
const cacheStore = useCacheStore()
const settingStore = useSettingStore()
const clientStore = useClientStore()
const searchStore = useSearchStore()
const sideMenu = useSideMenuStore()

const isValidUser = computed(
  () => userStore.profile && userStore.activeClient && clientStore.activeViewId,
)
const refresh = (): void => {
  eventBus.emit(REFRESH_HOME)
  eventBus.emit(REFRESH_MOTION)
}

const onLoad = (): void => {
  if (userStore.profile) {
    cacheStore.startCacheUpdate()
    settingStore.loadApplicationSettings()
  }
}

const onClientChange = async (): Promise<void> => {
  if (userStore.profile) {
    searchStore.isSearchActive = false
    searchStore.relatedId = undefined

    appStore.isIndeterminate = true
    await appStore.isClientReady(route.name)
    appStore.isIndeterminate = false

    refresh()
    sideMenu.close()
  }
}

const onViewChange = async (): Promise<void> => {
  appStore.isIndeterminate = true
  await appStore.isViewReady(route.name)
  appStore.isIndeterminate = false

  refresh()
}

watch(
  () => cacheStore.hasApplicationSettingUpdate,
  () => {
    if (cacheStore.hasApplicationSettingUpdate) {
      settingStore.loadApplicationSettings()
    }
  },
)

watch(
  () => cacheStore.hasClientSettingUpdate,
  () => {
    if (cacheStore.hasClientSettingUpdate) {
      clientStore.loadClientSettings(route.name)
    }
  },
)

watch(
  () => route.name,
  (newVal) => {
    clientStore.loadClientSettings(newVal)
  },
)

onLoad()
</script>

<template>
  <IndeterminateLoader class="fixed z-50 top-0 left-0" :loading="appStore.isIndeterminate" />
  <header
    class="z-40 fixed top-0 bg-black transition-[left] duration-200 shadow-md right-0 py-1"
    :class="isValidUser ? 'left-14' : 'left-0'"
  >
    <div class="flex flex-1 items-stretch justify-start">
      <div class="inline-flex items-center mx-4">
        <img :alt="$t('glossary.logo')" src="@/assets/images/logo.png" class="w-6 h-6" />
      </div>
      <div class="flex items-center text-2xl whitespace-nowrap tracking-wider">
        <span class="text-white">{{ $t('glossary.our') }}</span>
        <span class="text-primary">{{ $t('glossary.box', 2) }}</span>
      </div>
      <template v-if="isValidUser">
        <div class="flex flex-1 items-center justify-between ms-10">
          <div class="flex items-center">
            <ClientSelectInput
              v-model="userStore.activeClient"
              :title="$t('glossary.account')"
              :clients="userStore.clients"
              class="dark min-w-70"
              @change="onClientChange()"
            />
            <ViewMenu
              v-model="clientStore.activeViewId"
              :title="$t('glossary.view')"
              :views="clientStore.views"
              class="dark"
              @change="onViewChange()"
            />
          </div>
          <div class="flex items-center gap-2 me-3">
            <SearchBar />
            <AlertMenu />
            <UserNotificationMenu />
            <VersionUpdateMenu />
            <UserMenu />
          </div>
        </div>
      </template>
    </div>
  </header>
  <VersionUpdateDialog />
</template>
